// Role-set helpers for the maintenance interface.
//
// Server-side authorisation lives in tools/catalogue-client.mjs
// (requireRole, resolveRoles). The interface mirrors the same role
// strings client-side so it can hide affordances the user lacks the
// role for, per the hide-not-disable convention in
// INTERFACE-SPECIFICATION.md §12.2 and the role table in §13.2.
//
// The check here is a UX optimisation only; the proxy Worker re-runs
// requireRole() before any state change. A buggy or malicious client
// cannot bypass authorisation by stripping affordances.
//
// Strict role gating: admin is one role among many. It grants access-
// control management (data/access-control.json) and is the catch-all
// owner in CODEOWNERS for paths without a more specific reviewer. It
// does not implicitly grant proposer:* or reviewer:* roles — those
// must be held explicitly per the Authority Register
// (INTERFACE-SPECIFICATION.md §10.1, GOVERNANCE-ARCHITECTURE.md §9.1).
// The server requireRole() enforces this; the client must mirror or
// affordances appear that the server then rejects.

import type { ValueType } from './types';

export type RoleString =
  | 'admin'
  | 'alco'
  | 'viewer'
  | `proposer:${ValueType}`
  | `reviewer:${ValueType}`;

const VALUE_TYPES: ValueType[] = [
  'rates',
  'fees',
  'amounts',
  'terms',
  'purposes',
  'methodology',
  'schema',
  'dimensions'
];

export function hasRole(roles: readonly string[], role: string): boolean {
  return roles.includes(role);
}

export function hasAnyRole(roles: readonly string[], candidates: readonly string[]): boolean {
  return candidates.some((c) => roles.includes(c));
}

export function isAdmin(roles: readonly string[]): boolean {
  return roles.includes('admin');
}

export function isAlco(roles: readonly string[]): boolean {
  return roles.includes('alco');
}

export function isViewer(roles: readonly string[]): boolean {
  return roles.includes('viewer') || isAlco(roles) || isAdmin(roles) || hasAnyProposerOrReviewerRole(roles);
}

export function hasAnyProposerOrReviewerRole(roles: readonly string[]): boolean {
  return roles.some((r) => r.startsWith('proposer:') || r.startsWith('reviewer:'));
}

export function valueTypesProposable(roles: readonly string[]): ValueType[] {
  const out: ValueType[] = [];
  for (const vt of VALUE_TYPES) {
    if (roles.includes(`proposer:${vt}`)) out.push(vt);
  }
  return out;
}

export function valueTypesReviewable(roles: readonly string[]): ValueType[] {
  const out: ValueType[] = [];
  for (const vt of VALUE_TYPES) {
    if (roles.includes(`reviewer:${vt}`)) out.push(vt);
  }
  return out;
}

export interface Affordances {
  canProposeRates: boolean;
  canProposeFees: boolean;
  canProposeAmounts: boolean;
  canProposeTerms: boolean;
  canProposePurposes: boolean;
  canProposeMethodology: boolean;
  canProposeSchema: boolean;
  canProposeDimensions: boolean;
  canReviewAnything: boolean;
  reviewableValueTypes: ValueType[];
  proposableValueTypes: ValueType[];
  canRollback: boolean;
  canGenerateComplianceReport: boolean;
  canAuditByHash: boolean;
  canManageAccessControl: boolean;
  canViewLifecycleStatus: boolean;
  hideEmptyState: boolean;
}

export function affordancesFor(roles: readonly string[]): Affordances {
  const admin = isAdmin(roles);
  const proposable = valueTypesProposable(roles);
  const reviewable = valueTypesReviewable(roles);
  return {
    // Propose-action affordances are gated strictly on the corresponding
    // proposer:{value_type} role. Admin alone does NOT grant these — the
    // server-side requireRole() rejects the action and the client must
    // not surface affordances the server will reject. See §10.1
    // Authority Register: admins manage access; they do not propose
    // unless they also hold the value-type role explicitly.
    canProposeRates: proposable.includes('rates'),
    canProposeFees: proposable.includes('fees'),
    canProposeAmounts: proposable.includes('amounts'),
    canProposeTerms: proposable.includes('terms'),
    canProposePurposes: proposable.includes('purposes'),
    canProposeMethodology: proposable.includes('methodology'),
    canProposeSchema: proposable.includes('schema'),
    canProposeDimensions: proposable.includes('dimensions'),
    canReviewAnything: reviewable.length > 0,
    reviewableValueTypes: reviewable,
    proposableValueTypes: proposable,
    canRollback: proposable.length > 0,
    // Read-path affordances — viewer-class roles include alco and admin
    // since both are at-or-above the viewer level for read-only audit.
    canGenerateComplianceReport: admin || isAlco(roles) || isViewer(roles),
    canAuditByHash: admin || isAlco(roles) || isViewer(roles),
    canManageAccessControl: admin,
    canViewLifecycleStatus: true,
    hideEmptyState: false
  };
}

// Map a cube edit's value type to the proposer / reviewer role strings
// needed for that edit. Matches the constants in tools/catalogue-client.mjs.
export function proposerRoleFor(valueType: ValueType): RoleString {
  return `proposer:${valueType}` as const;
}

export function reviewerRoleFor(valueType: ValueType): RoleString {
  return `reviewer:${valueType}` as const;
}

// Rate-cube fields map to value-types: a rollback that changes
// `interest_rate_pa` is a rates-class proposal; one that changes
// `max_loan_amount` is an amounts-class proposal. A single cell-rollback
// may touch one or both, so the required-roles set is the union over
// the fields that actually differ.
export const RATE_CUBE_FIELD_TO_VALUE_TYPE: Record<'interest_rate_pa' | 'max_loan_amount', ValueType> = {
  interest_rate_pa: 'rates',
  max_loan_amount: 'amounts'
};

// Required-roles for a single rate-cube delta row in HistoryComparison.
// Returns the proposer roles a user must hold to roll back this row.
// Fields with no delta (or null delta) contribute no role requirement.
export function requiredRolesForRateCubeRow(args: {
  rateDelta: number | null;
  amountDelta: number | null;
}): RoleString[] {
  const out: RoleString[] = [];
  if (args.rateDelta !== null && args.rateDelta !== 0) {
    out.push(proposerRoleFor(RATE_CUBE_FIELD_TO_VALUE_TYPE.interest_rate_pa));
  }
  if (args.amountDelta !== null && args.amountDelta !== 0) {
    out.push(proposerRoleFor(RATE_CUBE_FIELD_TO_VALUE_TYPE.max_loan_amount));
  }
  return out;
}

// Does the role-set hold every role in `required`? An empty `required`
// set returns true (no roles needed). Used by HistoryComparison to gate
// per-row selectability for rollback.
export function holdsAllRoles(roles: readonly string[], required: readonly string[]): boolean {
  return required.every((r) => roles.includes(r));
}
